







































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { curDeviceService, DeviceService} from '@/services/DeviceService';
import {  Channel, Reading } from '@/types/core';
import Spinner from '@/components/Spinner.vue'
import moment from 'moment';
import VueError from '@/components/VueError.vue';

// @ts-ignore
import Loading from "@/components/Loading.vue";
import {Chart, ChartItem, registerables} from "chart.js";

@Component({
  components:{
    Loading,
    VueError,
    Spinner
  }
})
export default class ReadingChart extends Vue {

  @Prop({ default: '',  type: String}) deviceId!: string;
  @Prop({ default: () => {},  type: Object}) channel!: Channel;

  private deviceService:DeviceService = curDeviceService;

  private error:string = '';
  private chartWidth:number = 636;
  private chartHeight:number = 500;

  private chart:any;

  // we default to the last 7 days worth of data
  private fromTs:number = moment().subtract(7, 'days').valueOf();
  private toTs:number = moment().valueOf();
  private curStep:number = 0;
  private loading:boolean = false;

  private startSec:number = 0;
  private finishSec:number = 0;
  private intervalSec:number = 0;

  private currentZoomedStartSec:number = 0;
  private currentZoomedFinishSec:number = 0;
  private currentZoomedIntervalSec:number = 0;

  private prevZoomedStartSec:number = 0;
  private prevZoomedFinishSec:number = 0;
  private prevZoomedIntervalSec:number = 3600;

  private readingList:Reading[] = [];

  private chartData: any = {};

  private chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    onResize: this.adjustHeight,
    elements: {
      line: {
        tension: 0, // disables bezier curves
      }
    },
    tooltips: {
      intersect: true
    },
    scales: {
      xAxis: {
        type: 'time',
        distribution: 'linear',
        time: {
          unit: 'day',
          tooltipFormat: 'D, HH:mm:ss z',
        }
      },
      yAxis: {
        beginAtZero: true,
        ticks: {
          // Include a dollar sign in the ticks
          callback: (value: any) => {
            return this.channel?.unit ? `${value} ${this.channel.unit}` : value;
          },
        }
      },
    },
  };

  @Watch('channel')
  private async onNewChannel(newChannel:Channel, oldChannel:Channel) {
    this.readingList = [];
    this.curStep = 0;
    await this.updateTimeRanges();
  }

  get id(): string {
    return `device-chart-canvas`;
  }

  created() {
    Chart.register(...registerables);
  }

  async mounted() {
    // tippy('[data-tippy-content]');
    const ctx = document.getElementById(this.id);
    this.createChart(ctx as ChartItem);
  }

  private createChart(ctx: ChartItem) {
    this.chart = new Chart(ctx, {
      type: 'line',
      data: this.chartData,
      options: this.chartOptions,
    });
  }

  private async refreshChart(deviceId: string,
    channelId: number,
    startSec: number,
    finishSec: number,
    intervalSec: number) {

    this.readingList = await this.refreshData();

    const chartPointList = this.readingList.map((reading) => ({x: reading.ts, y: reading.value}));

    chartPointList.unshift({x:this.fromTs - 1 * 60 * 60 * 1000, y: null});
    chartPointList.push({x:this.toTs + 1 * 60 * 60 * 1000, y: null});

    const chartData = {
      labels: this.readingList.map((reading) => reading.ts),
      datasets: [
        {
          pointRadius: 1,
          borderWidth: 2,
          borderColor: '#17a2b8',
          pointHitRadius: 10,
          fill: false,
          label: this.formatLabel(this.channel),
          data: chartPointList
        }
      ]
    };

    this.chart.data = chartData;
    this.chart.update();
  }

  async refreshData() {
    // nothing to do
    if(this.deviceId === undefined || this.channel.channelId === undefined)
      return [];

    let rawReadingList:Reading[] = [];

    this.loading = true;
    try {
      rawReadingList = await this.deviceService.getDeviceChannelReadings({
        deviceId: this.deviceId,
        channelId: this.channel.channelId,
        fromTs: this.fromTs,
        toTs: this.toTs
      });
    } catch(err) {
      this.error = `Unable to retrieve readings: ${(err as Error).message}`;
      return [];
    } finally {
      this.loading = false;
    }

    return this.filterData(rawReadingList);
  }

  filterData(src:Reading[]):Reading[] {
    const limit = 10000;

    if(src.length < limit)
      return src;

    let retList:Reading[] = [];

    let filterLevel = 0.001;
    
    while(filterLevel <= 0.01) {
      
      retList = src.filter((curElem, index) => {

        if(index === 0 || src[index + 1] === undefined)
          return true;

        const nextElem = src[index + 1];

        return Math.abs(nextElem.value - curElem.value) > (curElem.value * filterLevel);
      });

      if(retList.length < limit)
        return retList;

      filterLevel += 0.001;
    }

    console.log(`Using filter level ${filterLevel}: ${retList.length} items from ${src.length}`);

    return retList;
  }

  goBack() {
    this.curStep--;
    this.updateTimeRanges();
  }

  canGoBack():boolean {
    return this.channel.channelId !== undefined;
  }

  canGoForward():boolean {
    return this.channel.channelId !== undefined && (this.curStep !== 0);
  }

  goForward() {
    this.curStep++;
    this.updateTimeRanges();
  }

  adjustHeight(){
      this.chartHeight = document.documentElement.clientWidth < 576 ? 400 : 500
  }

  get fromDate() {
    return moment(this.fromTs).format('D MMM');
  }

  get toDate() {
    return moment(this.toTs).format('D MMM');
  }

  private async updateTimeRanges() {
    console.log(`Showing between -${7 + 7 * Math.abs(this.curStep)} days ago and -${7 * Math.abs(this.curStep)}`);
    this.fromTs = moment().subtract(7 + 7 * Math.abs(this.curStep), 'days').valueOf();
    this.toTs = moment().subtract(7 * Math.abs(this.curStep), 'days').valueOf();
    await this.refreshChart(this.deviceId,
        this.channel.channelId,
        this.startSec,
        this.finishSec,
        this.intervalSec);
  }

  private formatLabel(ch:Channel) {
    let ret = `Ch ${ch.channelId} - ${ch.type}`;

    if(ch.label)
      ret += ` - ${ch.label}`;

    return ret;
  }

  private formatTs(ts: number): string {
    return moment(ts).format('DD/MM/YYYY, HH:mm');
  }

}
